import React from 'react';
import { Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type { ConvmemStatus } from '../../api/convmemApi';

// conversation-memory ingest pipeline card (HOME-198): surfaces the ingest
// DLQ depth (and the main queue depth as a backlog indicator) so a failed
// embedding shows up here instead of sitting unnoticed in SQS. Alerting is a
// separate, host-side concern (cal/deployment/convmem-dlq-check/) — this
// widget is read-only, same as the rest of the Overview tab.
//
// Until HOME-243 this component fetched its own status directly from the
// browser (fetchConvmemStatus, on the same REFRESH_MS timer as the rest of
// the tab). It's now a passive display component like BoxCard: OverviewTab
// fetches the whole Overview payload — including this reading — from the
// portal API's GET /overview in one request, and passes the shaped result
// down as `status`.

interface Props {
    /** undefined = still loading the first result. */
    status: ConvmemStatus | undefined;
}

function depthChip(label: string, depth: number | undefined, alertOnNonZero: boolean) {
    if (depth === undefined) {
        return <Chip size='small' icon={<CircularProgress size={12} />} label={`${label}: checking…`} variant='outlined' />;
    }
    const isProblem = alertOnNonZero && depth > 0;
    return (
        <Chip
            size='small'
            color={isProblem ? 'error' : 'success'}
            label={`${label}: ${depth}`}
        />
    );
}

const ConvmemCard: React.FC<Props> = ({ status }) => {
    const reachabilityChip = () => {
        if (status === undefined) {
            return <Chip size='small' icon={<CircularProgress size={12} />} label='checking…' variant='outlined' />;
        }
        if (!status.reachable) {
            return <Chip size='small' color='error' label={`unreachable${status.error ? ` — ${status.error}` : ''}`} />;
        }
        const when = status.reportedAt ? new Date(status.reportedAt).toLocaleTimeString() : undefined;
        return <Chip size='small' color='success' label={when ? `online · ${when}` : 'online'} />;
    };

    const dlqNonZero = status?.reachable && (status.dlqDepth ?? 0) > 0;

    return (
        <Paper sx={{ p: 2 }} data-testid='convmem-card'>
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 0.5 }}>
                <Box>
                    <Typography variant='h6'>conversation-memory ingest</Typography>
                    <Typography variant='caption' color='text.secondary'>
                        hook → outbox → forwarder → SQS → Cal consumer → Luke pgvector
                    </Typography>
                </Box>
                {reachabilityChip()}
            </Stack>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                A non-empty dead-letter queue means some conversation content was
                captured but never embedded — it&apos;s missing from search until a
                human inspects and redrives it (HOME-198).
            </Typography>

            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                {depthChip('Queue depth', status?.reachable ? status.queueDepth : undefined, false)}
                {depthChip('DLQ depth', status?.reachable ? status.dlqDepth : undefined, true)}
            </Stack>

            {dlqNonZero && (
                <Typography variant='caption' sx={{ color: '#ef5350', display: 'block', mt: 1.5 }}>
                    DLQ non-empty — a Pushover alert fires from Cal
                    (convmem-dlq-check.timer) until this is redriven.
                </Typography>
            )}
        </Paper>
    );
};

export default ConvmemCard;
